import { useState, useEffect } from "react";
import type { PopularRecommendation } from "../interfaces";
import { categoryItems } from "../helpers";
import { getPopularRecommendations } from "../api-client";
import {
  Table,
  Select,
  Box,
  Flex,
  Text,
  Spinner,
  Link,
  Portal,
  createListCollection,
  Heading,
} from "@chakra-ui/react";

const categoryCollection = createListCollection({ items: categoryItems });

export function PopularRecommendations() {
  const [popularRecs, setPopularRecs] = useState<PopularRecommendation[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("Book");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const selectedItem = categoryItems.find(
    (item) => item.value === selectedCategory,
  );

  useEffect(() => {
    const fetchPopular = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getPopularRecommendations(selectedCategory) as PopularRecommendation[];
        setPopularRecs(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPopular();
  }, [selectedCategory]);

  return (
    <Box w="full" my={8}>
      <Heading as="h2" size="2xl" mb={4}>
        Most Popular Recommendations
      </Heading>
      <Flex mb={4}>
        <Select.Root
          collection={categoryCollection}
          value={[selectedCategory]}
          onValueChange={(details) => {
            const newCategory = details.value[0];
            if (newCategory) {
              setSelectedCategory(newCategory);
            }
          }}
          width="180px"
        >
          <Select.HiddenSelect />
          <Select.Label>Select a category</Select.Label>
          <Select.Control>
            <Select.Trigger>
              <Flex align="center" gap={2}>
                {selectedItem && <selectedItem.icon />}
                <Select.ValueText placeholder="Select a Category" />
              </Flex>
            </Select.Trigger>
          </Select.Control>
          <Portal>
            <Select.Positioner>
              <Select.Content>
                {categoryCollection.items.map((item) => (
                  <Select.Item key={item.value} item={item}>
                    <Flex as="span" align="center" gap={2}>
                      <item.icon />
                      <Select.ItemText>{item.label}</Select.ItemText>
                    </Flex>
                    <Select.ItemIndicator />
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Portal>
        </Select.Root>
      </Flex>
      {loading ? (
        <Flex justify="center" align="center" h="200px">
          <Spinner size="xl" />
        </Flex>
      ) : error ? (
        <Text color="red.500" textAlign="center">
          {error}
        </Text>
      ) : (
        <Table.Root size="lg" interactive colorPalette="purple">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Title</Table.ColumnHeader>
              <Table.ColumnHeader>Category</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="end">Count</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {popularRecs.map((rec) => (
              <Table.Row key={rec.id}>
                <Table.Cell>
                  <Link href={rec.url} target="_blank">
                    {rec.title}
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  <Flex align="center" gap={2}>
                    {(() => {
                      const Icon = categoryItems.find(
                        (item) => item.value === rec.category,
                      )?.icon;
                      return Icon ? <Icon /> : null;
                    })()}
                    {rec.category}
                  </Flex>
                </Table.Cell>
                <Table.Cell textAlign="end">
                  {rec.recommendation_count}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </Box>
  );
}
